import type { Connector } from '../../../util/PostMessageConnector';
import type { ApiInitArgs, OnApiUpdate } from '../../types';
import type {
  AllMethods,
  MethodArgsWithMaybePrefix,
  MethodResponseWithMaybePrefix,
} from '../../types/methods';

import { logDebugApi, logDebugError } from '../../../util/logs';
import { createConnector, createExtensionConnector } from '../../../util/PostMessageConnector';
import { pause } from '../../../util/schedulers';
import { trackWalletApiOperation } from '../../../util/walletTelemetry';
import { IS_IOS } from '../../../util/windowEnvironment';
import { createWindowProvider, createWindowProviderForExtension } from '../../../util/windowProvider';
import { POPUP_PORT } from '../extension/config';

const HEALTH_CHECK_TIMEOUT = 1500;
const HEALTH_CHECK_MIN_DELAY = 5000; // 5 sec
const HEALTH_CHECK_RETRY_DELAY = 1000;
const ACTIVE_DRAFT_GRACE_MS = 20_000;
const RECONNECT_WAIT_MS = 5000;
const API_RPC_TIMEOUT = 120_000;

let updateCallback: OnApiUpdate;
let worker: Worker | undefined;
let connector: Connector | undefined;
let isInitialized = false;
let initPromise: Promise<void> | undefined;
let activeDraftCount = 0;
let activeDraftStartedAt = 0;
let activeSubmissionCount = 0;
let healthCheckDeferred = false;
let healthCheckTimer: ReturnType<typeof setTimeout> | undefined;
let isHealthChecking = false;

export function initApi(onUpdate: OnApiUpdate, initArgs: ApiInitArgs) {
  updateCallback = onUpdate;

  if (!connector) {
    // We use process.env.IS_EXTENSION instead of IS_EXTENSION in order to remove the irrelevant code during bundling
    if (process.env.IS_EXTENSION) {
      const onReconnect = () => {
        initPromise = connector!.init(initArgs);
      };

      connector = createExtensionConnector(POPUP_PORT, onUpdate, undefined, onReconnect, API_RPC_TIMEOUT);

      createWindowProviderForExtension();
    } else {
      worker = new Worker(
        /* webpackChunkName: "worker" */ new URL('./provider.ts', import.meta.url),
      );
      connector = createConnector(worker, onUpdate, undefined, undefined, API_RPC_TIMEOUT);

      createWindowProvider(worker);
    }
  }

  if (!isInitialized) {
    if (IS_IOS) {
      setupIosHealthCheck();
    }
    isInitialized = true;
  }

  initPromise = connector.init(initArgs);
}

export async function callApi<T extends keyof AllMethods>(
  fnName: T,
  ...args: MethodArgsWithMaybePrefix<T>
) {
  const startedAt = Date.now();
  const isDraft = fnName === 'checkTransactionDraft';
  const isSubmission = /^(submit|confirm|sign)/.test(String(fnName)) && fnName !== 'submitWalletTelemetry';
  if (isDraft) {
    if (!activeDraftCount) activeDraftStartedAt = startedAt;
    activeDraftCount++;
  }
  if (isSubmission) activeSubmissionCount++;

  try {
    for (let attempt = 0; attempt < (isDraft ? 2 : 1); attempt++) {
      let activeConnector = connector;
      let activeInitPromise = initPromise;
      if ((!activeConnector || !activeInitPromise) && isDraft && attempt === 0) {
        await waitForConnector();
        activeConnector = connector;
        activeInitPromise = initPromise;
      }
      if (!activeConnector || !activeInitPromise) {
        const error = new Error('API is not initialized');
        logDebugError(`callApi: ${fnName}`, error);
        trackWalletApiOperation(
          String(fnName), 'error', Date.now() - startedAt, 'TransportError', error, 'initialization',
        );
        return undefined;
      }

      let stage: 'initialization' | 'worker_request' = 'initialization';
      try {
        await activeInitPromise;
        stage = 'worker_request';
        const result = await (activeConnector.request({
          name: fnName,
          args,
        }) as Promise<MethodResponseWithMaybePrefix<T>>);

        logDebugApi(`callApi: ${fnName}`, args, result);
        const outcome = result && typeof result === 'object' && 'error' in result ? 'error' : 'success';
        trackWalletApiOperation(
          String(fnName), outcome, Date.now() - startedAt,
          outcome === 'error' ? 'ApiResultError' : undefined,
          outcome === 'error' && result && typeof result === 'object' && 'error' in result
            ? result.error : undefined,
          outcome === 'error' ? stage : undefined,
        );
        return result;
      } catch (err) {
        // Args may contain signed transactions or credentials; only report sanitized error metadata.
        logDebugError(`callApi: ${fnName}`, err);
        trackWalletApiOperation(String(fnName), 'error', Date.now() - startedAt, 'TransportError', err, stage);
        if (!isDraft || attempt > 0 || !isRetryableDraftFailure(err)) return undefined;

        if (activeConnector !== connector || isConnectorDestroyed(err)) {
          if (!await waitForConnector(activeConnector)) return undefined;
        } else {
          await pause(HEALTH_CHECK_RETRY_DELAY);
        }
      }
    }
    return undefined;
  } finally {
    if (isDraft) activeDraftCount--;
    if (isSubmission) activeSubmissionCount--;
    if (!activeDraftCount && !activeSubmissionCount && healthCheckDeferred) {
      healthCheckDeferred = false;
      if (healthCheckTimer) clearTimeout(healthCheckTimer);
      healthCheckTimer = undefined;
      queueMicrotask(() => void ensureWorkerPing());
    }
  }
}

function isConnectorDestroyed(error: unknown) {
  return error instanceof Error && error.message === 'Connector destroyed';
}

function isRetryableDraftFailure(error: unknown) {
  return error instanceof Error && (
    isConnectorDestroyed(error)
    || error.message === 'Connector request timed out'
    || error.message === 'Failed to fetch'
    || error.name === 'NetworkError'
  );
}

async function waitForConnector(previous?: Connector) {
  const deadline = Date.now() + RECONNECT_WAIT_MS;
  while (Date.now() < deadline) {
    if (connector && initPromise && connector !== previous) return true;
    await pause(100);
  }
  return false;
}

export async function callApiWithThrow<T extends keyof AllMethods>(
  fnName: T,
  ...args: MethodArgsWithMaybePrefix<T>
) {
  const startedAt = Date.now();
  const activeConnector = connector;
  const activeInitPromise = initPromise;
  let stage: 'initialization' | 'worker_request' = 'initialization';
  try {
    if (!activeConnector || !activeInitPromise) throw new Error('API is not initialized');
    await activeInitPromise;
    stage = 'worker_request';
    return await (activeConnector.request({
      name: fnName,
      args,
    }) as Promise<MethodResponseWithMaybePrefix<T>>);
  } catch (err) {
    if (fnName !== 'ping') {
      trackWalletApiOperation(String(fnName), 'error', Date.now() - startedAt, 'TransportError', err, stage);
    }
    throw err;
  }
}

const startedAt = Date.now();

// Workaround for iOS sometimes stops interacting with worker
function setupIosHealthCheck() {
  window.addEventListener('focus', () => {
    void ensureWorkerPing();
  });
}

async function ensureWorkerPing() {
  if (isHealthChecking) return;
  if (activeSubmissionCount || (activeDraftCount && Date.now() - activeDraftStartedAt < ACTIVE_DRAFT_GRACE_MS)) {
    deferHealthCheck();
    return;
  }
  isHealthChecking = true;
  const checkedConnector = connector;
  try {
    if (!checkedConnector) return;
    for (let attempt = 0; attempt < 2; attempt++) {
      let isResolved = false;
      try {
        await Promise.race([
          callApiWithThrow('ping'),
          pause(HEALTH_CHECK_TIMEOUT)
            .then(() => (isResolved ? undefined : Promise.reject(new Error('HEALTH_CHECK_TIMEOUT')))),
        ]);
        return;
      } catch (err) {
        logDebugError('ensureWorkerPing', err);
        trackWalletApiOperation('ping', 'error', HEALTH_CHECK_TIMEOUT, 'TransportError', err, 'worker_request');
        if (connector !== checkedConnector) return;
        if (activeSubmissionCount || (activeDraftCount && Date.now() - activeDraftStartedAt < ACTIVE_DRAFT_GRACE_MS)) {
          deferHealthCheck();
          return;
        }
        if (attempt === 0) await pause(HEALTH_CHECK_RETRY_DELAY);
      } finally {
        isResolved = true;
      }
    }

    if (Date.now() - startedAt < HEALTH_CHECK_MIN_DELAY) {
      deferHealthCheck();
    } else if (connector === checkedConnector && !activeSubmissionCount
      && (!activeDraftCount || Date.now() - activeDraftStartedAt >= ACTIVE_DRAFT_GRACE_MS)) {
      checkedConnector.destroy();
      worker?.terminate();
      worker = undefined;
      connector = undefined;
      initPromise = undefined;
      updateCallback({ type: 'requestReconnectApi' });
    }
  } finally {
    isHealthChecking = false;
    if (healthCheckDeferred && !activeDraftCount && !activeSubmissionCount) {
      healthCheckDeferred = false;
      if (healthCheckTimer) clearTimeout(healthCheckTimer);
      healthCheckTimer = undefined;
      queueMicrotask(() => void ensureWorkerPing());
    }
  }
}

function deferHealthCheck() {
  healthCheckDeferred = true;
  if (healthCheckTimer) clearTimeout(healthCheckTimer);
  if (!activeSubmissionCount) {
    const delay = activeDraftCount
      ? Math.max(100, ACTIVE_DRAFT_GRACE_MS - (Date.now() - activeDraftStartedAt))
      : Math.max(100, HEALTH_CHECK_MIN_DELAY - (Date.now() - startedAt));
    healthCheckTimer = setTimeout(() => {
      healthCheckDeferred = false;
      void ensureWorkerPing();
    }, delay);
  }
}
